import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, share, withLatestFrom } from 'rxjs/operators';

import * as CompaniesActions from '@collections/companies';
import { PlatformService } from '@core/services/platform.service';
import { isTruthy } from '@shared/utils';

import { getGlobalSearchResultsWithCompanies } from './store/layout.selectors';

const MIN_SEARCH_LENGTH = 3;

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private globalSearchSubject = new Subject<string>();

  globalSearchResults$ = this.store.select(getGlobalSearchResultsWithCompanies);

  globalSearch$ = this.globalSearchSubject.pipe(
    filter(isTruthy),
    debounceTime(500),
    distinctUntilChanged(),
    filter((val: string) => val.length >= MIN_SEARCH_LENGTH),
    share(),
  );

  globalSearchMobile$ = this.globalSearch$.pipe(
    withLatestFrom(this.platformService.isMobileView$),
    filter(([, isMobile]) => isMobile),
    map(([search]) => search),
  );

  globalSearchDesktop$ = this.globalSearch$.pipe(
    withLatestFrom(this.platformService.isMobileView$),
    filter(([, isMobile]) => !isMobile),
    map(([search]) => search),
  );

  constructor(private store: Store<{}>, private platformService: PlatformService) {
    this.globalSearchResults$
      .pipe(
        distinctUntilChanged(
          (prev, curr) =>
            prev.jobs.length && curr.jobs.length && prev.jobs[0].id === curr.jobs[0].id && prev.jobs.length === curr.jobs.length,
        ),
      )
      .subscribe(({ jobs }) => {
        jobs.forEach((job) => {
          if (!job.company && job.companyId) {
            this.store.dispatch(CompaniesActions.companiesGet({ id: job.companyId }));
          }
        });
      });
  }

  emitGlobalSearch(search: string) {
    this.globalSearchSubject.next(search);
  }
}
